import { usePageTitle } from "@/lib/usePageTitle";
import { ArrowRight, Clock, Heart, Users, Home as HomeIcon } from "lucide-react";
import FadeIn from "@/components/site/FadeIn";
import { PrimaryLink, PageHero } from "@/components/site/ui";
import Icon from "@/components/site/Icon";
import { useLocale } from "@/i18n/LocaleContext";

const TEAM_IMG = "/images/team-pflege.jpg";

export default function UeberUns() {
  usePageTitle("about");
  const { t, path } = useLocale();
  return (
    <div data-testid="about-page">
      <PageHero
        eyebrow={t.about.eyebrow}
        title={t.about.title}
        subtitle={t.about.subtitle}
      />

      {/* Intro + Bild */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-16 md:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <FadeIn>
              <h2 className="text-3xl md:text-4xl font-600 text-brand-dark leading-snug">
                {t.about.introTitle}
              </h2>
              <div className="mt-5 space-y-4 text-lg text-muted-foreground leading-relaxed">
                {t.about.intro.map((para) => (
                  <p key={para.slice(0, 24)}>{para}</p>
                ))}
              </div>
            </FadeIn>
            <FadeIn delay={0.15}>
              <figure>
                <img
                  src={TEAM_IMG}
                  alt={t.about.teamImgAlt}
                  loading="lazy"
                  className="w-full h-[420px] object-cover rounded-2xl shadow-[0_20px_60px_rgb(44,51,51,0.15)]"
                />
                <figcaption className="mt-2 text-end text-sm text-muted-foreground">{t.common.symbolImage}</figcaption>
              </figure>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Werte */}
      <section className="bg-cream">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-16 md:py-24">
          <FadeIn className="max-w-2xl">
            <h2 className="text-3xl md:text-4xl font-600 text-brand-dark leading-snug">
              {t.about.valuesTitle}
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">{t.about.valuesLead}</p>
          </FadeIn>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.values.map((v, i) => (
              <FadeIn key={v.label} delay={(i % 4) * 0.08}>
                <div className="flex flex-col items-start gap-4 rounded-2xl bg-white p-7 border border-border shadow-[0_8px_30px_rgb(44,51,51,0.06)] h-full">
                  <span className="flex items-center justify-center w-14 h-14 rounded-2xl bg-brand-light text-brand">
                    <Icon name={v.icon} className="w-7 h-7" />
                  </span>
                  <span className="font-serif text-xl font-600 text-brand-dark">
                    {v.label}
                  </span>
                </div>
              </FadeIn>
            ))}
          </div>

          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {t.about.pillars.map((b, i) => (
              <FadeIn key={b.title} delay={i * 0.1}>
                <div className="rounded-2xl bg-white p-8 border border-border h-full">
                  <span className="flex items-center justify-center w-12 h-12 rounded-xl bg-coral/15 text-coral-hover">
                    <Icon name={b.icon} className="w-6 h-6" />
                  </span>
                  <h3 className="mt-4 text-xl font-600 text-brand-dark">{b.title}</h3>
                  <p className="mt-2 text-base text-muted-foreground leading-relaxed">{b.text}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-5 md:px-10 py-16 md:py-20 text-center">
          <FadeIn>
            <h2 className="text-3xl md:text-4xl font-600 text-brand-dark">
              {t.about.ctaTitle}
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">{t.about.ctaLead}</p>
            <div className="mt-8 flex justify-center">
              <PrimaryLink to={path("contact")} data-testid="about-contact-cta">
                {t.common.contactUs} <ArrowRight className="w-5 h-5 rtl:rotate-180" />
              </PrimaryLink>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
